import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { RefreshCw, ExternalLink, User, Mail, Building, Calendar, MessageSquare, Tag, Bot, Clock, Loader2, Bell, BellOff, AlertCircle, CheckCircle2, XCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { format } from 'date-fns'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'

interface LinkedInReplyDetailsDialogProps {
  reply: any | null
  isOpen: boolean
  onClose: () => void
  onRefresh?: () => void
  onCategorize?: (replyId: string) => void
  categorizingIds?: Set<string>
}

export function LinkedInReplyDetailsDialog({
  reply,
  isOpen,
  onClose,
  onRefresh,
  onCategorize,
  categorizingIds = new Set()
}: LinkedInReplyDetailsDialogProps) {
  const [sentMessage, setSentMessage] = useState<any | null>(null)
  const [loadingSentMessage, setLoadingSentMessage] = useState(false)
  const [sendingNotification, setSendingNotification] = useState(false)
  const [notificationStatus, setNotificationStatus] = useState<{
    status: 'sent' | 'not_sent' | 'skipped' | 'unknown'
    reason?: string
  } | null>(null)
  
  // Slack Notifications state
  const [slackNotifications, setSlackNotifications] = useState<any[]>([])
  const [loadingNotifications, setLoadingNotifications] = useState(false)

  // Helper function to extract latest reply from recent_messages
  const getLatestReply = () => {
    if (!reply?.recent_messages || !Array.isArray(reply.recent_messages)) {
      return null
    }
    
    // Find the latest message where is_reply is true
    const replyMessages = reply.recent_messages
      .filter((msg: any) => msg.is_reply === true)
      .sort((a: any, b: any) => {
        // Try multiple timestamp field names
        const timeA = a.timestamp || a.created_at || a.creation_time || a.time || 0
        const timeB = b.timestamp || b.created_at || b.creation_time || b.time || 0
        return timeB - timeA
      })
    
    return replyMessages.length > 0 ? replyMessages[0] : null
  }

  // Helper function to get timestamp from a message object
  const getMessageTimestamp = (message: any) => {
    // Try multiple timestamp field names in order of preference
    return message?.timestamp || 
           message?.created_at || 
           message?.creation_time || 
           message?.time ||
           null
  }

  // Helper function to get reply count
  // Uses the pre-calculated reply_count column (set by categorize-linkedin-reply function)
  // Falls back to counting recent_messages if reply_count is not available
  const getReplyCount = () => {
    if (reply?.reply_count != null && reply.reply_count > 0) {
      return reply.reply_count
    }
    // Fallback: count from recent_messages
    if (!reply?.recent_messages || !Array.isArray(reply.recent_messages)) {
      return 0
    }
    return reply.recent_messages.filter((msg: any) => msg.is_reply === true).length
  }

  // Fetch sent message for this reply
  useEffect(() => {
    const fetchSentMessage = async () => {
      if (!reply || !isOpen) {
        setSentMessage(null)
        return
      }

      // Need campaign_id, lead_profile_url, sender_profile_url, and reply timestamp
      if (!reply.campaign_id || !reply.lead_profile_url || !reply.sender_profile_url || !reply.timestamp) {
        setSentMessage(null)
        return
      }

      setLoadingSentMessage(true)
      try {

        // Query for message_sent event matching:
        // - Same campaign_id
        // - Same lead_profile_url
        // - Same sender_profile_url
        // - Timestamp BEFORE the reply timestamp
        const { data, error } = await supabase
          .from('heyreach_linkedin_events')
          .select(`
            id,
            campaign_id,
            event_type,
            timestamp,
            connection_message,
            recent_messages,
            data,
            created_at
          `)
          .eq('event_type', 'message_sent')
          .eq('campaign_id', reply.campaign_id)
          .eq('lead_profile_url', reply.lead_profile_url)
          .eq('sender_profile_url', reply.sender_profile_url)
          .lt('timestamp', reply.timestamp) // Sent message must be before reply
          .order('timestamp', { ascending: false })
          .limit(1) // Get the most recent sent message before the reply

        if (error) {
          console.error('❌ Error fetching sent message:', error)
          setSentMessage(null)
          return
        }

        if (data && data.length > 0) {
          const sentEvent = data[0]
          
          // For message_sent events, the message content is typically in recent_messages array
          // where is_reply = false. It can also be in connection_message or data fields.
          let messageContent = null
          
          // First, try to get from recent_messages array (most common for message_sent)
          if (sentEvent.recent_messages && Array.isArray(sentEvent.recent_messages)) {
            const sentMessageInArray = sentEvent.recent_messages.find(
              (msg: any) => msg.is_reply === false || msg.is_reply === null || msg.is_reply === undefined
            )
            if (sentMessageInArray) {
              messageContent = sentMessageInArray.message || sentMessageInArray.text || sentMessageInArray.content
            }
          }
          
          // Fallback to connection_message
          if (!messageContent) {
            messageContent = sentEvent.connection_message
          }
          
          // Fallback to data.message or data.connection_message
          if (!messageContent) {
            messageContent = sentEvent.data?.message || sentEvent.data?.connection_message
          }
          
          // Fallback to data.recent_messages if available
          if (!messageContent && sentEvent.data?.recent_messages && Array.isArray(sentEvent.data.recent_messages)) {
            const sentMessageInData = sentEvent.data.recent_messages.find(
              (msg: any) => msg.is_reply === false || msg.is_reply === null || msg.is_reply === undefined
            )
            if (sentMessageInData) {
              messageContent = sentMessageInData.message || sentMessageInData.text || sentMessageInData.content
            }
          }
          
          console.log('✅ Found sent message:', {
            id: sentEvent.id,
            timestamp: sentEvent.timestamp,
            has_message: !!messageContent,
            has_recent_messages: !!sentEvent.recent_messages,
            recent_messages_length: sentEvent.recent_messages?.length || 0,
            has_connection_message: !!sentEvent.connection_message,
            message_preview: messageContent ? messageContent.substring(0, 50) + '...' : null,
            recent_messages_structure: sentEvent.recent_messages ? JSON.stringify(sentEvent.recent_messages, null, 2).substring(0, 200) : null,
            data_structure: sentEvent.data ? Object.keys(sentEvent.data) : null
          })

          setSentMessage({
            id: sentEvent.id,
            timestamp: sentEvent.timestamp,
            created_at: sentEvent.created_at,
            message: messageContent,
            connection_message: sentEvent.connection_message,
            recent_messages: sentEvent.recent_messages,
            data: sentEvent.data
          })
        } else {
          setSentMessage(null)
        }
      } catch (error) {
        console.error('❌ Error in fetchSentMessage:', error)
        setSentMessage(null)
      } finally {
        setLoadingSentMessage(false)
      }
    }

    fetchSentMessage()
  }, [reply, isOpen])

  // Fetch notification status
  useEffect(() => {
    const fetchNotificationStatus = async () => {
      if (!reply?.id) {
        setNotificationStatus(null)
        return
      }

      try {
        const { data, error } = await supabase
          .from('heyreach_linkedin_events')
          .select('slack_notification_sent, reply_category_type, reply_count')
          .eq('id', reply.id)
          .single()

        if (error) {
          console.error('Error fetching notification status:', error)
          setNotificationStatus({ status: 'unknown' })
          return
        }

        if (data) {
          if (data.slack_notification_sent === true) {
            setNotificationStatus({ status: 'sent' })
          } else if (data.reply_category_type === 'auto-reply' || data.reply_category_type === 'OOO') {
            setNotificationStatus({ status: 'skipped', reason: `Skipped for ${data.reply_category_type} replies` })
          } else {
            setNotificationStatus({ status: 'not_sent', reason: 'Notification was not sent' })
          }
        }
      } catch (error) {
        console.error('Error in fetchNotificationStatus:', error)
        setNotificationStatus({ status: 'unknown' })
      }
    }

    fetchNotificationStatus()
  }, [reply?.id])

  // Fetch Slack notifications for this LinkedIn reply
  useEffect(() => {
    const fetchSlackNotifications = async () => {
      if (!reply || !isOpen) {
        setSlackNotifications([])
        return
      }
      
      setLoadingNotifications(true)
      try {
        // First, resolve client_id from campaign_id
        const clientId = await resolveClientId(reply.campaign_id)
        if (!clientId) {
          setSlackNotifications([])
          return
        }
        
        // Determine expected notification type based on reply category and count
        let expectedMessageTypes: string[] = []
        const replyCount = getReplyCount()
        
        if (reply.reply_category_type === 'positive') {
          if (replyCount === 1) {
            expectedMessageTypes = ['linkedinPositiveReplyNotification']
          } else if (replyCount === 2) {
            expectedMessageTypes = ['linkedinSecondReplyNotification']
          } else if (replyCount > 2) {
            expectedMessageTypes = ['linkedinFollowupReplyNotification']
          }
        } else if (reply.reply_category_type === 'negative') {
          expectedMessageTypes = ['linkedinNegativeReplyNotification']
        } else if (reply.reply_category_type === 'neutral') {
          expectedMessageTypes = ['linkedinNeutralReplyNotification']
        }
        
        if (expectedMessageTypes.length === 0) {
          setSlackNotifications([])
          return
        }
        
        // Get the categorized_at timestamp to find notifications sent around that time
        // Use a wider window: 1 hour before to 24 hours after categorization
        const categorizedAt = reply.categorized_at || reply.created_at
        const startTime = categorizedAt ? new Date(new Date(categorizedAt).getTime() - 60 * 60 * 1000).toISOString() : null // 1 hour before
        const endTime = categorizedAt ? new Date(new Date(categorizedAt).getTime() + 24 * 60 * 60 * 1000).toISOString() : null // 24 hours after
        
        // Get client UUID from client_id
        const { data: clientData } = await supabase
          .from('clients')
          .select('id')
          .eq('client_id', clientId)
          .maybeSingle()
        
        if (!clientData) {
          setSlackNotifications([])
          return
        }
        
        let query = supabase
          .from('slack_message_logs')
          .select('*')
          .eq('client_id', clientData.id)
          .in('message_type', expectedMessageTypes)
          .order('sent_at', { ascending: false })
          .limit(10) // Limit to most recent 10 notifications of this type
        
        // Apply time window if we have categorization timestamp
        // Otherwise, just get recent notifications (last 7 days)
        if (startTime && endTime) {
          query = query.gte('sent_at', startTime).lte('sent_at', endTime)
        } else {
          // If no categorization timestamp, look at last 7 days
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
          query = query.gte('sent_at', sevenDaysAgo)
        }
        
        const { data, error } = await query
        
        if (error) {
          console.error('Error fetching Slack notifications:', error)
          setSlackNotifications([])
        } else {
          setSlackNotifications(data || [])
        }
      } catch (err) {
        console.error('Error fetching Slack notifications:', err)
        setSlackNotifications([])
      } finally {
        setLoadingNotifications(false)
      }
    }
    
    fetchSlackNotifications()
  }, [reply?.id, reply?.campaign_id, reply?.reply_category_type, isOpen])
  
  // Resolve client_id from campaign_id
  const resolveClientId = async (campaignId: number | string | null): Promise<number | null> => {
    if (!campaignId) return null

    try {
      const campaignIdStr = String(campaignId)
      
      // Step 1: Look up ab_test_variants by heyreach_campaign_id
      let { data: variant, error: variantError } = await supabase
        .from('ab_test_variants')
        .select('segment_id, configuration')
        .eq('heyreach_campaign_id', campaignIdStr)
        .eq('channel', 'linkedin')
        .maybeSingle()
      
      // Fallback: Try to extract from configuration->campaign_url
      if (!variant && !variantError) {
        const { data: variantsWithoutHeyreachId } = await supabase
          .from('ab_test_variants')
          .select('id, segment_id, configuration')
          .is('heyreach_campaign_id', null)
          .eq('channel', 'linkedin')
        
        if (variantsWithoutHeyreachId) {
          for (const v of variantsWithoutHeyreachId) {
            const campaignUrl = v.configuration?.campaign_url
            if (campaignUrl) {
              const patterns = [
                /\/campaign\/view\/(\d+)/,
                /\/campaign\/create\/(\d+)/,
                /\/campaign\/(\d+)/
              ]
              
              for (const pattern of patterns) {
                const match = campaignUrl.match(pattern)
                if (match && match[1] === campaignIdStr) {
                  variant = { segment_id: v.segment_id, configuration: v.configuration }
                  break
                }
              }
              if (variant) break
            }
          }
        }
      }
      
      if (variantError || !variant?.segment_id) {
        return null
      }
      
      // Step 2: Look up ab_test_segments
      const { data: segment, error: segmentError } = await supabase
        .from('ab_test_segments')
        .select('campaign_id')
        .eq('id', variant.segment_id)
        .maybeSingle()
      
      if (segmentError || !segment?.campaign_id) {
        return null
      }
      
      // Step 3: Look up campaigns
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('client_id')
        .eq('id', segment.campaign_id)
        .maybeSingle()
      
      if (campaignError || !campaign?.client_id) {
        return null
      }
      
      // Step 4: Look up clients to get numeric client_id
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('client_id')
        .eq('id', campaign.client_id)
        .maybeSingle()
      
      if (clientError || !client?.client_id) {
        return null
      }
      
      return typeof client.client_id === 'string' 
        ? parseInt(client.client_id) || client.client_id
        : client.client_id
    } catch (error) {
      console.error('Error resolving client_id:', error)
      return null
    }
  }

  // Determine notification type
  const getNotificationType = (replyCategoryType: string | null, replyCount: number) => {
    const isPositive = replyCategoryType === 'positive'
    const isNegative = replyCategoryType === 'negative'
    const isNeutral = replyCategoryType === 'neutral'
    const isSecondReply = replyCount === 2
    const isFollowupReply = replyCount > 2
    
    if (isFollowupReply && isPositive) {
      return { type: 'linkedinFollowupReplyNotification', channel: 'positive_replies' }
    } else if (isSecondReply && isPositive) {
      return { type: 'linkedinSecondReplyNotification', channel: 'positive_replies' }
    } else if (isPositive) {
      return { type: 'linkedinPositiveReplyNotification', channel: 'positive_replies' }
    } else if (isNegative) {
      return { type: 'linkedinNegativeReplyNotification', channel: 'neutral_negative_replies' }
    } else if (isNeutral) {
      return { type: 'linkedinNeutralReplyNotification', channel: 'neutral_negative_replies' }
    } else {
      return { type: 'linkedinNeutralReplyNotification', channel: 'neutral_negative_replies' }
    }
  }

  // Handle manual notification send
  const handleSendNotification = async () => {
    if (!reply?.id || !reply?.campaign_id) {
      toast({
        title: 'Error',
        description: 'Reply ID and Campaign ID are required',
        variant: 'destructive'
      })
      return
    }

    // Skip if auto-reply or OOO
    if (reply.reply_category_type === 'auto-reply' || reply.reply_category_type === 'OOO') {
      toast({
        title: 'Notification Skipped',
        description: `Notifications are not sent for ${reply.reply_category_type} replies`,
        variant: 'default'
      })
      return
    }

    setSendingNotification(true)
    try {
      // Resolve client_id
      const clientId = await resolveClientId(reply.campaign_id)
      
      if (!clientId) {
        toast({
          title: 'Cannot Send Notification',
          description: 'Could not resolve client_id from campaign. Please check campaign configuration.',
          variant: 'destructive'
        })
        return
      }

      // Determine notification type
      const { type: notificationType, channel: channelType } = getNotificationType(
        reply.reply_category_type,
        replyCount
      )

      // Send notification
      const { data, error } = await supabase.functions.invoke('scheduled-slack-reports', {
        body: {
          reportType: notificationType,
          clientId: clientId.toString(),
          channelType: channelType,
          dryRun: false,
          replyId: reply.id,
          triggerSource: 'manual_notification_from_details_dialog'
        }
      })

      if (error) {
        throw error
      }

      if (data?.success) {
        // Mark as sent
        await supabase
          .from('heyreach_linkedin_events')
          .update({ slack_notification_sent: true })
          .eq('id', reply.id)

        setNotificationStatus({ status: 'sent' })
        toast({
          title: 'Notification Sent',
          description: `${notificationType} sent successfully to ${channelType} channel`,
        })

        // Refresh the reply data
        if (onRefresh) {
          onRefresh()
        }
      } else {
        throw new Error(data?.error || 'Notification failed')
      }
    } catch (error) {
      console.error('Error sending notification:', error)
      toast({
        title: 'Notification Failed',
        description: error instanceof Error ? error.message : 'Failed to send notification',
        variant: 'destructive'
      })
    } finally {
      setSendingNotification(false)
    }
  }

  const latestReply = getLatestReply()
  const replyCount = getReplyCount()

  if (!reply) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle>LinkedIn Reply Details</DialogTitle>
              <DialogDescription>
                Complete information for this LinkedIn reply
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Debug Refresh Button */}
              {onRefresh && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  className="mr-2"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Data
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0 px-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
            </TabsList>
            
            <div className="flex-1 min-h-0 overflow-hidden px-6">
              <ScrollArea className="h-full w-full">
                <div className="w-full pr-4">
                  <TabsContent value="overview" className="space-y-6 w-full">
                    {/* Lead Profile */}
                    <div className="bg-white border rounded-lg p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                          {reply.lead_full_name?.charAt(0).toUpperCase() || 'L'}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold">
                            {reply.lead_full_name || 'Unknown Lead'}
                          </h3>
                          {reply.lead_profile_url && (
                            <a 
                              href={reply.lead_profile_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm flex items-center gap-1 mt-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View LinkedIn Profile
                            </a>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              LinkedIn Reply
                            </span>
                            {reply.response_category && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                reply.reply_category_type === 'positive' ? 'bg-green-100 text-green-800' :
                                reply.reply_category_type === 'negative' ? 'bg-red-100 text-red-800' :
                                reply.reply_category_type === 'OOO' ? 'bg-yellow-100 text-yellow-800' :
                                reply.reply_category_type === 'auto-reply' ? 'bg-gray-100 text-gray-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {reply.response_category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Lead Timeline */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900">Timeline</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">Campaign Outreach</div>
                              <div className="text-xs text-gray-500">
                                {reply.campaign_name || 'Unknown Campaign'}
                                {reply.campaign_id && (
                                  <span className="ml-2 text-blue-600">(ID: {reply.campaign_id})</span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {reply.created_at ? new Date(reply.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">Reply Received</div>
                              <div className="text-xs text-gray-500">
                                Reply Count: {replyCount}
                                {reply.response_category && (
                                  <span className="ml-2">
                                    • {reply.response_category}
                                    {reply.reply_category_type && (
                                      <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                                        reply.reply_category_type === 'positive' ? 'bg-green-200 text-green-800' :
                                        reply.reply_category_type === 'negative' ? 'bg-red-200 text-red-800' :
                                        reply.reply_category_type === 'OOO' ? 'bg-yellow-200 text-yellow-800' :
                                        reply.reply_category_type === 'auto-reply' ? 'bg-gray-200 text-gray-800' :
                                        'bg-blue-200 text-blue-800'
                                      }`}>
                                        {reply.reply_category_type}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {reply.created_at ? new Date(reply.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sender Information */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Sender Information</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sender Name:</span>
                          <span className="font-medium">{reply.sender_full_name || 'N/A'}</span>
                        </div>
                        {reply.sender_profile_url && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">LinkedIn Profile:</span>
                            <a 
                              href={reply.sender_profile_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View Profile
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notification Status */}
                    {reply.reply_category_type && (
                      <div className="bg-white border rounded-lg p-6">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Bell className="w-4 h-4" />
                          Slack Notification Status
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Status:</span>
                            <div className="flex items-center gap-2">
                              {notificationStatus?.status === 'sent' && (
                                <>
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  <span className="font-medium text-green-600">Sent</span>
                                </>
                              )}
                              {notificationStatus?.status === 'not_sent' && (
                                <>
                                  <XCircle className="w-4 h-4 text-red-600" />
                                  <span className="font-medium text-red-600">Not Sent</span>
                                </>
                              )}
                              {notificationStatus?.status === 'skipped' && (
                                <>
                                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                                  <span className="font-medium text-yellow-600">Skipped</span>
                                </>
                              )}
                              {(!notificationStatus || notificationStatus.status === 'unknown') && (
                                <>
                                  <BellOff className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium text-gray-500">Unknown</span>
                                </>
                              )}
                            </div>
                          </div>
                          {notificationStatus?.reason && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Reason:</span>
                              <span className="text-sm text-gray-500">{notificationStatus.reason}</span>
                            </div>
                          )}
                          {notificationStatus?.status === 'not_sent' && (
                            <div className="pt-3 border-t">
                              <Button
                                onClick={handleSendNotification}
                                disabled={sendingNotification || !reply.campaign_id}
                                className="w-full"
                                variant="default"
                              >
                                {sendingNotification ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Sending Notification...
                                  </>
                                ) : (
                                  <>
                                    <Bell className="w-4 h-4 mr-2" />
                                    Send Notification Now
                                  </>
                                )}
                              </Button>
                              {!reply.campaign_id && (
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                  Campaign ID is required to send notification
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Reply Information */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Reply Information</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reply Count:</span>
                          <span className="font-medium">{replyCount}</span>
                        </div>
                        {reply.response_category && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Response Category:</span>
                              <span className="font-medium">{reply.response_category}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Category ID:</span>
                              <span className="font-medium">{reply.response_category_id || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Category Type:</span>
                              <span className="font-medium">{reply.reply_category_type || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">AI Model Used:</span>
                              <span className="font-medium">{reply.ai_model_used || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Categorized At:</span>
                              <span className="font-medium">
                                {reply.categorized_at ? new Date(reply.categorized_at).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                            {reply.reasoning && (
                              <div className="pt-2 border-t">
                                <div className="text-gray-600 mb-1">AI Reasoning:</div>
                                <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                  {reply.reasoning}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Event Type:</span>
                          <span className="font-medium">{reply.event_type || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Created At:</span>
                          <span className="font-medium">
                            {reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        {reply.response_category && (
                          <>
                            <div className="pt-3 mt-3 border-t">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">AI Categorization</h5>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Response Category:</span>
                                  <span className="font-medium">{reply.response_category}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Category ID:</span>
                                  <span className="font-medium">{reply.response_category_id || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Category Type:</span>
                                  <span className="font-medium">{reply.reply_category_type || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">AI Model Used:</span>
                                  <span className="font-medium">{reply.ai_model_used || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Categorized At:</span>
                                  <span className="font-medium">
                                    {reply.categorized_at ? new Date(reply.categorized_at).toLocaleString() : 'N/A'}
                                  </span>
                                </div>
                                {reply.reasoning && (
                                  <div className="pt-2 mt-2 border-t">
                                    <div className="text-gray-600 mb-1 text-xs">AI Reasoning:</div>
                                    <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded">
                                      {reply.reasoning}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Campaign Context */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Campaign Context</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Campaign ID:</span>
                          <span className="font-medium">{reply.campaign_id || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Campaign Name:</span>
                          <span className="font-medium">{reply.campaign_name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Slack Notifications Section */}
                    <div className="bg-white border rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900">Slack Notifications</h4>
                        <Bell className="w-5 h-5 text-purple-600" />
                      </div>
                      {loadingNotifications ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Loading notifications...</span>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Primary notification status from slack_notification_sent flag */}
                          {reply.reply_category_type ? (
                            <div className="border rounded-lg p-3 bg-gray-50">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">
                                      {getReplyCount() === 1 && reply.reply_category_type === 'positive' && '1st Positive Reply'}
                                      {getReplyCount() === 2 && reply.reply_category_type === 'positive' && '2nd Positive Reply'}
                                      {getReplyCount() > 2 && reply.reply_category_type === 'positive' && 'Follow-up Reply'}
                                      {reply.reply_category_type === 'negative' && 'Negative Reply'}
                                      {reply.reply_category_type === 'neutral' && 'Neutral Reply'}
                                      {!reply.reply_category_type && 'Reply Notification'}
                                    </span>
                                    {reply.slack_notification_sent ? (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                        Sent
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                        Not Sent
                                      </span>
                                    )}
                                  </div>
                                  {reply.slack_notification_sent && reply.categorized_at && (
                                    <div className="text-sm text-gray-600">
                                      Sent after categorization: {new Date(reply.categorized_at).toLocaleString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        timeZone: 'Europe/London'
                                      })}
                                    </div>
                                  )}
                                  {reply.slack_notification_sent && slackNotifications.length > 0 && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Found {slackNotifications.length} matching notification(s) in logs
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              Reply not yet categorized - notification will be sent after categorization
                            </div>
                          )}
                          
                          {/* Additional notifications from logs (if any) */}
                          {slackNotifications.length > 0 && (
                            <div className="mt-4">
                              <div className="text-xs font-medium text-gray-700 mb-2">Additional Notifications Found:</div>
                              {slackNotifications.map((notification, index) => (
                                <div key={notification.id || index} className="border rounded-lg p-3 bg-blue-50 mb-2">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium text-gray-900">
                                          {notification.message_type === 'linkedinPositiveReplyNotification' && '1st Positive Reply'}
                                          {notification.message_type === 'linkedinSecondReplyNotification' && '2nd Positive Reply'}
                                          {notification.message_type === 'linkedinFollowupReplyNotification' && 'Follow-up Reply'}
                                          {notification.message_type === 'linkedinNegativeReplyNotification' && 'Negative Reply'}
                                          {notification.message_type === 'linkedinNeutralReplyNotification' && 'Neutral Reply'}
                                        </span>
                                        {notification.success ? (
                                          <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                            Sent
                                          </span>
                                        ) : (
                                          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                            Failed
                                          </span>
                                        )}
                                      </div>
                                      {notification.sent_at && (
                                        <div className="text-xs text-gray-600">
                                          {new Date(notification.sent_at).toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'Europe/London'
                                          })}
                                        </div>
                                      )}
                                      {notification.channel_type && (
                                        <div className="mt-1 text-xs text-gray-500">
                                          Channel: {notification.channel_type}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="messages" className="space-y-4 w-full h-full flex flex-col">
                    {/* LinkedIn Message Thread */}
                    <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                      {/* Message Header */}
                      <div className="border-b border-gray-200 p-4 bg-gray-50 flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              LinkedIn Conversation
                            </h3>
                            {replyCount > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                {replyCount} {replyCount === 1 ? 'Reply' : 'Replies'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>Lead: {reply.lead_full_name || 'Unknown'}</span>
                            {reply.lead_profile_url && (
                              <a 
                                href={reply.lead_profile_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span>Sender: {reply.sender_full_name || 'Unknown'}</span>
                            {reply.sender_profile_url && (
                              <a 
                                href={reply.sender_profile_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-4">
                          {/* Loading State */}
                          {loadingSentMessage && (
                            <div className="flex items-center justify-center py-8 text-gray-500">
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              <span>Loading sent message...</span>
                            </div>
                          )}

                          {/* Sent Message (if found) */}
                          {sentMessage && !loadingSentMessage && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                {reply.sender_full_name?.charAt(0).toUpperCase() || 'S'}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-900">Sent Message</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">
                                    {sentMessage.timestamp 
                                      ? new Date(sentMessage.timestamp).toLocaleString()
                                      : sentMessage.created_at
                                      ? new Date(sentMessage.created_at).toLocaleString()
                                      : 'N/A'}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words bg-blue-50 p-3 rounded-lg border border-blue-100">
                                  {sentMessage.message || 
                                   sentMessage.connection_message || 
                                   (sentMessage.recent_messages && Array.isArray(sentMessage.recent_messages) 
                                     ? sentMessage.recent_messages
                                         .find((msg: any) => !msg.is_reply)?.message || 
                                       sentMessage.recent_messages[0]?.message
                                     : null) ||
                                   (sentMessage.data?.recent_messages && Array.isArray(sentMessage.data.recent_messages)
                                     ? sentMessage.data.recent_messages
                                         .find((msg: any) => !msg.is_reply)?.message ||
                                       sentMessage.data.recent_messages[0]?.message
                                     : null) ||
                                   sentMessage.data?.message ||
                                   sentMessage.data?.connection_message ||
                                   'No message content'}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Latest Reply */}
                          {latestReply && (
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                {reply.lead_full_name?.charAt(0).toUpperCase() || 'L'}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-900">Latest Reply</span>
                                  <span className="text-xs text-gray-500">•</span>
                                  <span className="text-xs text-gray-500">
                                    {(() => {
                                      // Priority order:
                                      // 1. Message object timestamp (from recent_messages array)
                                      // 2. Reply event timestamp (when the reply event was received)
                                      // 3. Reply event created_at
                                      // 4. Reply time_replied field
                                      const messageTimestamp = getMessageTimestamp(latestReply)
                                      const eventTimestamp = reply.timestamp || reply.time_replied || reply.created_at
                                      const timestamp = messageTimestamp || eventTimestamp
                                      
                                      if (timestamp) {
                                        try {
                                          const date = new Date(timestamp)
                                          if (isNaN(date.getTime())) {
                                            return 'N/A'
                                          }
                                          return date.toLocaleString()
                                        } catch (e) {
                                          return 'N/A'
                                        }
                                      }
                                      
                                      return 'N/A'
                                    })()}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words bg-blue-50 p-3 rounded-lg border border-blue-100">
                                  {latestReply.message || latestReply.text || latestReply.content || 'No message content'}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* All Replies */}
                          {reply.recent_messages && Array.isArray(reply.recent_messages) && reply.recent_messages.length > 1 && (
                            <div className="mt-6 pt-6 border-t">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3">All Replies in Conversation</h4>
                              <div className="space-y-3">
                                {reply.recent_messages
                                  .filter((msg: any) => msg.is_reply === true)
                                  .sort((a: any, b: any) => {
                                    const timeA = a.timestamp || a.created_at || 0
                                    const timeB = b.timestamp || b.created_at || 0
                                    return timeB - timeA
                                  })
                                  .map((msg: any, index: number) => (
                                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                        {reply.lead_full_name?.charAt(0).toUpperCase() || 'L'}
                                      </div>
                                        <div className="flex-1">
                                          <div className="text-xs text-gray-500 mb-1">
                                            {(() => {
                                              const msgTimestamp = getMessageTimestamp(msg) || reply.timestamp || reply.created_at
                                              if (msgTimestamp) {
                                                try {
                                                  return new Date(msgTimestamp).toLocaleString()
                                                } catch (e) {
                                                  return 'N/A'
                                                }
                                              }
                                              return 'N/A'
                                            })()}
                                          </div>
                                        <div className="text-sm text-gray-700">
                                          {msg.message || msg.text || msg.content || 'No message content'}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                          {/* Empty State */}
                          {!loadingSentMessage && !sentMessage && !latestReply && (
                            <div className="text-center py-12 text-gray-500">
                              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                              <p>No messages found</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </ScrollArea>
            </div>
          </Tabs>
        </div>

        {/* AI Categorization Footer - Fixed at bottom of dialog, always visible */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
            {reply?.response_category ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {reply.response_category}
                    </span>
                    {reply.reply_category_type && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        reply.reply_category_type === 'positive' ? 'bg-green-100 text-green-700' :
                        reply.reply_category_type === 'negative' ? 'bg-red-100 text-red-700' :
                        reply.reply_category_type === 'OOO' ? 'bg-yellow-100 text-yellow-700' :
                        reply.reply_category_type === 'auto-reply' ? 'bg-gray-100 text-gray-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {reply.reply_category_type}
                      </span>
                    )}
                  </div>
                  {reply.reasoning && (
                    <div className="text-xs text-gray-500 max-w-md truncate" title={reply.reasoning}>
                      {reply.reasoning}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {onCategorize && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onCategorize(reply.id)}
                            disabled={categorizingIds.has(reply.id) || !reply.campaign_id}
                          >
                            {categorizingIds.has(reply.id) ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Re-categorizing...
                              </>
                            ) : (
                              <>
                                <Tag className="w-4 h-4 mr-2" />
                                Re-categorize
                              </>
                            )}
                          </Button>
                        </TooltipTrigger>
                        {!reply.campaign_id && (
                          <TooltipContent>
                            <p>Campaign ID is required for categorization</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Not categorized</span>
                </div>
                {onCategorize && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => onCategorize(reply.id)}
                          disabled={categorizingIds.has(reply.id) || !reply.campaign_id}
                        >
                          {categorizingIds.has(reply.id) ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Categorizing...
                            </>
                          ) : (
                            <>
                              <Tag className="w-4 h-4 mr-2" />
                              Categorize Reply
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      {!reply.campaign_id && (
                        <TooltipContent>
                          <p>Campaign ID is required for categorization</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
